import type { Inventory } from '../inventory';
import { ITEMS } from '../inventory';
import type { MissionEventBus } from './MissionEventBus';

/**
 * Subscribes to one or more inventories and republishes the combined
 * sellable value as a `inventoryValueChanged` event whenever the totals
 * shift. Lets mission steps gate on "you have enough cargo to afford
 * tool X" without coupling to inventory internals.
 *
 * Sellable value mirrors what the trade terminal would actually pay —
 * `count * ITEMS[id].sellValue` for every sellable slot.
 */
export class InventoryValueWatcher {
  private readonly inventories: readonly Inventory[];
  private readonly bus: MissionEventBus;
  private readonly unsubscribes: Array<() => void> = [];
  private lastValue = -1;

  constructor(inventories: readonly Inventory[], bus: MissionEventBus) {
    this.inventories = inventories;
    this.bus = bus;
    for (const inv of inventories) {
      this.unsubscribes.push(inv.subscribe(() => this.handle()));
    }
    // The very first inv.subscribe call delivers a snapshot synchronously,
    // so the constructor already pushed an initial emit. Force a final
    // emit at the end so the value reflects the union after every
    // inventory has registered, not just the first.
    this.handle();
  }

  /** Force a re-emit. Useful right after a save/load when the
   *  inventory state has been swapped wholesale; the inventory will
   *  emit on its own, but a manual nudge ensures listeners that
   *  attached late still receive a baseline. */
  refresh(): void {
    this.lastValue = -1;
    this.handle();
  }

  dispose(): void {
    for (const unsub of this.unsubscribes) unsub();
    this.unsubscribes.length = 0;
  }

  /** Current combined sellable value across all watched inventories.
   *  Skips items that aren't sellable so the number maps cleanly to
   *  "credits the player would receive at the Market". */
  computeValue(): number {
    let total = 0;
    for (const inv of this.inventories) {
      for (const slot of inv.snapshot()) {
        if (!slot) continue;
        const def = ITEMS[slot.itemId];
        if (def?.sellable) total += slot.count * def.sellValue;
      }
    }
    return total;
  }

  private handle(): void {
    const value = this.computeValue();
    if (value === this.lastValue) return;
    this.lastValue = value;
    this.bus.emit('inventoryValueChanged', { value });
  }
}
